import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import ClientOperatingAnalysis from 'components/client_operating_analysis/ClientOperatingAnalysis';
import { AuthContext } from 'contexts/auth_context/AuthContext';
import { languageContext } from 'contexts/language_context/LanguageContext';
import { themeContext } from 'contexts/theme_context/ThemeContext';
import FleetOverview from 'pages/tables/FleetOverview';
import {
    PRODUCT_ANALYTICS_EVENTS,
    configureProductAnalytics,
    trackProductAnalyticsEvent,
} from 'services/productAnalyticsService';
import CustomerProfileHeader from './CustomerProfileHeader';
import CustomerPortalPanelState from './CustomerPortalPanelState';
import ReleaseNotesModal from './ReleaseNotesModal';
import { mergeCustomerPortalCopy } from './customerPortalCopy';
import useCustomerProfile from './useCustomerProfile';
import './CustomerPortal.css';

const OPERATING_ANALYSIS_SECTION = 'operating-analysis';
const FLEET_OVERVIEW_SECTION = 'fleet-overview';
const DEFAULT_SECTION = OPERATING_ANALYSIS_SECTION;

const sectionOrder = [OPERATING_ANALYSIS_SECTION, FLEET_OVERVIEW_SECTION];

const getSectionKey = (sectionId) => (
    sectionId === FLEET_OVERVIEW_SECTION ? 'fleetOverview' : 'operatingAnalysis'
);

const getNextSection = (currentSection, direction) => {
    const currentIndex = sectionOrder.indexOf(currentSection);
    if (currentIndex < 0) return DEFAULT_SECTION;

    const nextIndex = (currentIndex + direction + sectionOrder.length) % sectionOrder.length;
    return sectionOrder[nextIndex];
};

export default function CustomerPortal() {
    const { section = DEFAULT_SECTION } = useParams();
    const navigate = useNavigate();
    const { webtheme } = useContext(themeContext);
    const { weblanguage, jsonfile } = useContext(languageContext);
    const authContext = useContext(AuthContext) || {};
    const { token, readCookie } = authContext;
    const tabRefs = useRef({});
    const portalOpenedTrackedRef = useRef(false);
    const [visitedSections, setVisitedSections] = useState(() => (
        new Set([DEFAULT_SECTION, sectionOrder.includes(section) ? section : DEFAULT_SECTION])
    ));
    const [vehicleFilter, setVehicleFilter] = useState(null);
    const {
        profileState,
        analysisClient,
        isProfilePending,
        isAnalysisScopeReady,
    } = useCustomerProfile({ token, readCookie });
    const isRtl = weblanguage === 'ar';
    const locale = weblanguage === 'ar' ? 'ar-EG' : 'en-US';
    const customerPortalCopy = useMemo(() => (
        mergeCustomerPortalCopy(jsonfile?.[weblanguage]?.customerPortal)
    ), [jsonfile, weblanguage]);
    const tableCopy = jsonfile?.[weblanguage]?.table || {};

    const isKnownSection = sectionOrder.includes(section);
    const activeSection = isKnownSection ? section : DEFAULT_SECTION;
    const authToken = useMemo(() => (
        token || (typeof readCookie === 'function' ? readCookie() : null) || ''
    ), [readCookie, token]);

    useEffect(() => {
        if (!isKnownSection) return;

        setVisitedSections((currentSections) => {
            if (currentSections.has(section)) return currentSections;
            const nextSections = new Set(currentSections);
            nextSections.add(section);
            return nextSections;
        });
    }, [isKnownSection, section]);

    useEffect(() => {
        configureProductAnalytics({ token: authToken });
    }, [authToken]);

    useEffect(() => {
        if (!authToken || portalOpenedTrackedRef.current) return;

        portalOpenedTrackedRef.current = true;
        trackProductAnalyticsEvent(PRODUCT_ANALYTICS_EVENTS.PORTAL_OPENED, {
            token: authToken,
            page: `/customer-portal/${activeSection}`,
            properties: {
                section: activeSection,
            },
        });
    }, [activeSection, authToken]);

    useEffect(() => {
        if (!authToken || !isKnownSection) return;

        trackProductAnalyticsEvent(PRODUCT_ANALYTICS_EVENTS.PORTAL_SECTION_VIEWED, {
            token: authToken,
            page: `/customer-portal/${activeSection}`,
            properties: {
                section: activeSection,
            },
        });

        if (activeSection === FLEET_OVERVIEW_SECTION) {
            trackProductAnalyticsEvent(PRODUCT_ANALYTICS_EVENTS.FLEET_OVERVIEW_OPENED, {
                token: authToken,
                page: `/customer-portal/${activeSection}`,
                properties: {
                    section: activeSection,
                },
            });
        }
    }, [activeSection, authToken, isKnownSection]);

    if (!isKnownSection) {
        return <Navigate to={`/customer-portal/${DEFAULT_SECTION}`} replace />;
    }

    const tabs = sectionOrder.map((sectionId) => ({
        id: sectionId,
        label: customerPortalCopy.sections[getSectionKey(sectionId)],
    }));

    const navigateToSection = (sectionId, shouldFocus = false) => {
        navigate(`/customer-portal/${sectionId}`);
        if (shouldFocus) {
            window.setTimeout(() => {
                tabRefs.current[sectionId]?.focus();
            }, 0);
        }
    };

    const handleVehicleFilterChange = (nextVehicleFilter) => {
        setVehicleFilter(nextVehicleFilter);
        if (!authToken || !nextVehicleFilter) return;

        trackProductAnalyticsEvent(PRODUCT_ANALYTICS_EVENTS.FLEET_VEHICLE_SELECTED, {
            token: authToken,
            page: `/customer-portal/${FLEET_OVERVIEW_SECTION}`,
            properties: {
                section: FLEET_OVERVIEW_SECTION,
            },
        });
    };

    const handleTabKeyDown = (event, sectionId) => {
        const isForward = (isRtl && event.key === 'ArrowLeft') || (!isRtl && event.key === 'ArrowRight');
        const isBackward = (isRtl && event.key === 'ArrowRight') || (!isRtl && event.key === 'ArrowLeft');

        if (isForward || isBackward) {
            event.preventDefault();
            navigateToSection(getNextSection(sectionId, isForward ? 1 : -1), true);
            return;
        }

        if (event.key === 'Home') {
            event.preventDefault();
            navigateToSection(sectionOrder[0], true);
            return;
        }

        if (event.key === 'End') {
            event.preventDefault();
            navigateToSection(sectionOrder[sectionOrder.length - 1], true);
        }
    };

    return (
        <main className={`customer-portal-page ${webtheme}-theme`} dir={isRtl ? 'rtl' : 'ltr'}>
            <CustomerProfileHeader
                copy={customerPortalCopy}
                locale={locale}
                profileState={profileState}
            />

            <div className="customer-portal-tabs" role="tablist" aria-label={customerPortalCopy.title}>
                {tabs.map((tab) => {
                    const isActive = activeSection === tab.id;
                    return (
                        <button
                            id={`customer-portal-tab-${tab.id}`}
                            key={tab.id}
                            type="button"
                            className={`customer-portal-tab${isActive ? ' active' : ''}`}
                            ref={(element) => { tabRefs.current[tab.id] = element; }}
                            role="tab"
                            aria-selected={isActive}
                            aria-controls={`customer-portal-panel-${tab.id}`}
                            tabIndex={isActive ? 0 : -1}
                            onClick={() => navigateToSection(tab.id)}
                            onKeyDown={(event) => handleTabKeyDown(event, tab.id)}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            <section
                id={`customer-portal-panel-${OPERATING_ANALYSIS_SECTION}`}
                className="customer-portal-panel"
                role="tabpanel"
                aria-labelledby={`customer-portal-tab-${OPERATING_ANALYSIS_SECTION}`}
                hidden={activeSection !== OPERATING_ANALYSIS_SECTION}
            >
                {isAnalysisScopeReady ? (
                    <ClientOperatingAnalysis
                        analysisClient={analysisClient}
                        vehicleFilter={vehicleFilter}
                        filterCopy={tableCopy}
                        showCacheDetails={false}
                        autoLoadDefaultRange={activeSection === OPERATING_ANALYSIS_SECTION}
                        initialPresetKey="last30Days"
                        isClientScopeReady={isAnalysisScopeReady}
                    />
                ) : (
                    <CustomerPortalPanelState
                        status={isProfilePending ? 'loading' : 'error'}
                        title={isProfilePending
                            ? customerPortalCopy.profile.analysisLoadingTitle
                            : customerPortalCopy.profile.analysisUnavailableTitle}
                        message={isProfilePending
                            ? customerPortalCopy.profile.analysisLoadingMessage
                            : customerPortalCopy.profile.analysisUnavailableMessage}
                    />
                )}
            </section>

            {visitedSections.has(FLEET_OVERVIEW_SECTION) && (
                <section
                    id={`customer-portal-panel-${FLEET_OVERVIEW_SECTION}`}
                    className="customer-portal-panel"
                    role="tabpanel"
                    aria-labelledby={`customer-portal-tab-${FLEET_OVERVIEW_SECTION}`}
                    hidden={activeSection !== FLEET_OVERVIEW_SECTION}
                >
                    <FleetOverview
                        isEmbedded
                        showTitle
                        isActive={activeSection === FLEET_OVERVIEW_SECTION}
                        onVehicleFilterChange={handleVehicleFilterChange}
                    />
                </section>
            )}

            <ReleaseNotesModal
                token={authToken}
                copy={customerPortalCopy.releaseNotes}
                locale={locale}
            />
        </main>
    );
}
